/**
 * Portal-based popover that escapes overflow:hidden containers.
 *
 * - Auto-picks placement (below / above / right / left) from available space.
 * - Supports anchor-ref positioning OR arbitrary {x,y} point positioning.
 * - Arrow-key navigation across children marked with `data-popover-item`.
 * - First item highlighted by default; scrolls active item into view.
 * - Click-outside and Escape to close.
 */

import type { CSSProperties, ReactNode, RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getTopPanelZ } from "./DraggablePanel.tsx";
import { PanelSurfaceFrame } from "./panelSurface.tsx";
import { COLOR_ACTIVE_BG_SUBTLE, RADIUS_SM, Z_POPOVER } from "./tokens.ts";

const VIEWPORT_MARGIN = 8;
const ITEM_SELECTOR = "[data-popover-item]";
const MAX_HEIGHT_VH = 0.6; // popover will never exceed 60% of viewport height

// Module-level sequence counter that increments each time a Popover opens.
// Every newly-opened Popover captures the current value and uses it as a
// z-index offset, so a late-opened Popover (e.g. a SelectSearch dropdown
// rendered inside an already-open FloatingCommandMenu) always stacks
// above its parent. Never decremented — unbounded over a session, but
// bounded in practice and well below any real numeric limit.
let popoverOpenSequence = 0;

// Stable style tag id — shared by all Popover instances
const STYLE_ID = "__popover-active-style";

function ensureStyle() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `[data-popover-active="true"] { background: ${COLOR_ACTIVE_BG_SUBTLE} !important; border-radius: ${RADIUS_SM}px !important; }`;
  document.head.appendChild(style);
}

interface PopoverBaseProps {
  /** Whether the popover is shown. */
  open: boolean;
  /** Called when the popover should close. */
  onClose: () => void;
  /** Pixel gap between anchor and popover edge. */
  gap?: number;
  /**
   * Preferred placement relative to the anchor. Each value hugs one edge
   * of the anchor first, falls back to the opposite edge, then to a
   * perpendicular edge — never re-covering the preferred edge — so a
   * flyout hosted on a viewport edge (e.g. the ToolPalette on the left
   * edge) can never re-open back over its host surface.
   *   - `"below"` tries below → above → right → left (default)
   *   - `"above"` tries above → below → right → left
   *   - `"right"` tries right → left → below → above
   *   - `"left"`  tries left → right → below → above
   */
  placement?: "below" | "above" | "right" | "left";
  /** Extra styles merged onto the popover container. */
  style?: CSSProperties;
  /**
   * Optional callback to offset the anchor point so a specific child element
   * (e.g. the last-selected menu item) lands under the cursor.  Called during
   * reposition with the popover DOM element; returns the {x, y} offset from
   * the popover's top-left corner to the point that should align with the anchor.
   */
  getAnchorOffset?: (popoverEl: HTMLDivElement) => { x: number; y: number };
  /**
   * Optional forced viewport position, bypassing anchor/centered computation.
   * Consumed by draggable shells (FloatingCommandMenu) so they can override
   * the automatic positioning with a user-chosen drag location. `null` and
   * `undefined` both fall back to the default positioning.
   */
  positionOverride?: { top: number; left: number } | null;
  /**
   * Optional ref bound to the rendered popover root element. Consumers that
   * need to measure or drag the popover container use this to read its
   * bounding rect without forking the component.
   */
  rootRef?: RefObject<HTMLDivElement | null>;
  children: ReactNode;
}

interface AnchorRefProps extends PopoverBaseProps {
  /** Ref to the element the popover is anchored to. */
  anchorRef: RefObject<HTMLElement | null>;
  anchorPoint?: never;
  centered?: never;
}

interface AnchorPointProps extends PopoverBaseProps {
  /** Arbitrary viewport coordinate to anchor the popover at. */
  anchorPoint: { x: number; y: number };
  anchorRef?: never;
  centered?: never;
}

interface CenteredProps extends PopoverBaseProps {
  /** Center the popover in the viewport (no anchor). */
  centered: true;
  anchorRef?: never;
  anchorPoint?: never;
}

export type PopoverProps = AnchorRefProps | AnchorPointProps | CenteredProps;

export function Popover({
  anchorRef,
  anchorPoint,
  centered,
  open,
  onClose,
  gap = 4,
  placement = "below",
  style,
  getAnchorOffset,
  positionOverride,
  rootRef,
  children,
}: PopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  // Captured on each `open` transition so every newly-opened Popover
  // stacks above any already-open Popover (see popoverOpenSequence).
  const [capturedZ, setCapturedZ] = useState<number>(Z_POPOVER);
  const scrollingRef = useRef(false);
  const scrollTimerRef = useRef(0);

  // ------------------------------------------------------------------
  // Positioning
  // ------------------------------------------------------------------

  const reposition = useCallback(() => {
    const popover = popoverRef.current;
    if (!popover) return;

    // Explicit position override (drag-parked menu) — skip all anchor
    // math and clamp-to-viewport only as a final safety step.
    if (positionOverride) {
      const pr = popover.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let { top, left } = positionOverride;
      if (left + pr.width > vw - VIEWPORT_MARGIN)
        left = vw - VIEWPORT_MARGIN - pr.width;
      if (top + pr.height > vh - VIEWPORT_MARGIN)
        top = vh - VIEWPORT_MARGIN - pr.height;
      if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
      if (top < VIEWPORT_MARGIN) top = VIEWPORT_MARGIN;
      setPos({ top, left });
      return;
    }

    const pr = popover.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top: number;
    let left: number;

    if (centered) {
      // Viewport-centered — no anchor
      top = Math.round((vh - pr.height) / 2);
      left = Math.round((vw - pr.width) / 2);
    } else if (anchorPoint) {
      // Arbitrary point — place below-right, then adjust
      const offset = getAnchorOffset?.(popover);
      if (offset && (offset.x !== 0 || offset.y !== 0)) {
        // Recall mode: position so a specific item lands under the cursor
        top = anchorPoint.y - offset.y;
        left = anchorPoint.x - offset.x;
      } else {
        // Default: top-left at cursor, flip if needed
        top = anchorPoint.y;
        left = anchorPoint.x;
        // Flip up if not enough space below
        if (
          top + pr.height > vh - VIEWPORT_MARGIN &&
          anchorPoint.y - pr.height > VIEWPORT_MARGIN
        ) {
          top = anchorPoint.y - pr.height;
        }
        // Flip left if not enough space right
        if (
          left + pr.width > vw - VIEWPORT_MARGIN &&
          anchorPoint.x - pr.width > VIEWPORT_MARGIN
        ) {
          left = anchorPoint.x - pr.width;
        }
      }
    } else {
      const anchor = anchorRef?.current;
      if (!anchor) return;
      const ar = anchor.getBoundingClientRect();

      const spaceBelow = vh - ar.bottom - VIEWPORT_MARGIN;
      const spaceAbove = ar.top - VIEWPORT_MARGIN;
      const spaceRight = vw - ar.right - VIEWPORT_MARGIN;
      const spaceLeft = ar.left - VIEWPORT_MARGIN;

      if (placement === "right") {
        // Submenu semantics: hug the anchor's right edge; flip to the left
        // only when right doesn't fit and left is roomier. Falls back to
        // below/above only as a last resort so the flyout never stacks
        // over its parent menu.
        if (spaceRight >= pr.width || spaceRight >= spaceLeft) {
          top = ar.top;
          left = ar.right + gap;
        } else if (spaceLeft >= pr.width) {
          top = ar.top;
          left = ar.left - pr.width - gap;
        } else if (spaceBelow >= pr.height || spaceBelow >= spaceAbove) {
          top = ar.bottom + gap;
          left = ar.left;
        } else {
          top = ar.top - pr.height - gap;
          left = ar.left;
        }
      } else if (placement === "left") {
        if (spaceLeft >= pr.width || spaceLeft >= spaceRight) {
          top = ar.top;
          left = ar.left - pr.width - gap;
        } else if (spaceRight >= pr.width) {
          top = ar.top;
          left = ar.right + gap;
        } else if (spaceBelow >= pr.height || spaceBelow >= spaceAbove) {
          top = ar.bottom + gap;
          left = ar.left;
        } else {
          top = ar.top - pr.height - gap;
          left = ar.left;
        }
      } else if (placement === "above") {
        if (spaceAbove >= pr.height || spaceAbove >= spaceBelow) {
          top = ar.top - pr.height - gap;
          left = ar.left;
        } else if (spaceBelow >= pr.height) {
          top = ar.bottom + gap;
          left = ar.left;
        } else if (spaceRight >= pr.width || spaceRight >= spaceLeft) {
          top = ar.top;
          left = ar.right + gap;
        } else {
          top = ar.top;
          left = ar.left - pr.width - gap;
        }
      } else if (spaceBelow >= pr.height || spaceBelow >= spaceAbove) {
        top = ar.bottom + gap;
        left = ar.left;
      } else if (spaceAbove >= pr.height) {
        top = ar.top - pr.height - gap;
        left = ar.left;
      } else if (spaceRight >= pr.width || spaceRight >= spaceLeft) {
        top = ar.top;
        left = ar.right + gap;
      } else {
        top = ar.top;
        left = ar.left - pr.width - gap;
      }
    }

    // Clamp into viewport
    if (left + pr.width > vw - VIEWPORT_MARGIN)
      left = vw - VIEWPORT_MARGIN - pr.width;
    if (top + pr.height > vh - VIEWPORT_MARGIN)
      top = vh - VIEWPORT_MARGIN - pr.height;
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
    if (top < VIEWPORT_MARGIN) top = VIEWPORT_MARGIN;

    setPos({ top, left });
  }, [
    anchorRef,
    anchorPoint,
    centered,
    gap,
    placement,
    getAnchorOffset,
    positionOverride,
  ]);

  // Mount off-screen, then reposition after first frame. Each open also
  // bumps the module-level sequence so this Popover stacks above any
  // earlier-opened sibling (key for nested dropdowns inside menus).
  useEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    setActiveIdx(0);
    setCapturedZ(Z_POPOVER + getTopPanelZ() + ++popoverOpenSequence);
    setPos({ top: -9999, left: -9999 });
  }, [open]);

  // Re-run positioning whenever the override changes mid-session (drag).
  useEffect(() => {
    if (!open || !positionOverride || !pos || pos.top === -9999) return;
    reposition();
  }, [open, positionOverride, reposition, pos]);

  useEffect(() => {
    if (pos && pos.top === -9999) {
      requestAnimationFrame(reposition);
    }
  }, [pos, reposition]);

  // Re-position when popover content size changes (e.g. tab switch)
  useEffect(() => {
    const el = popoverRef.current;
    if (!open || !el || !pos || pos.top === -9999) return;
    const ro = new ResizeObserver(() => reposition());
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, pos, reposition]);

  // ------------------------------------------------------------------
  // Active-item DOM sync
  // ------------------------------------------------------------------

  useEffect(() => {
    ensureStyle();
  }, []);

  const getItems = useCallback(
    () =>
      popoverRef.current
        ? Array.from(
            popoverRef.current.querySelectorAll<HTMLElement>(ITEM_SELECTOR),
          )
        : [],
    [],
  );

  // Sync data-popover-active attribute & scroll into view.
  // scrollIntoView is suppressed while the user is scrolling so a pointer
  // entering an item mid-scroll doesn't snap the list back to the old item.
  useEffect(() => {
    if (!open) return;
    const items = getItems();
    items.forEach((el, i) => {
      el.setAttribute(
        "data-popover-active",
        i === activeIdx ? "true" : "false",
      );
    });
    if (!scrollingRef.current) {
      items[activeIdx]?.scrollIntoView({ block: "nearest" });
    }
  });

  // Pointer-enter on items updates activeIdx.
  // We re-bind when children change (pos settles), not on every activeIdx change.
  // Suppressed during scroll so items scrolling under the pointer don't fight
  // the user's scroll via the scrollIntoView sync effect.
  useEffect(() => {
    if (!open || !pos || pos.top === -9999) return;
    const items = getItems();
    const handlers = items.map((el, i) => {
      const handler = () => {
        if (!scrollingRef.current) setActiveIdx(i);
      };
      el.addEventListener("pointerenter", handler);
      return () => el.removeEventListener("pointerenter", handler);
    });
    return () => handlers.forEach((cleanup) => cleanup());
  }, [open, pos, getItems]);

  // Track scroll state so pointer-enter handlers can be suppressed.
  useEffect(() => {
    if (!open) return;
    const el = popoverRef.current;
    if (!el) return;
    const onScroll = () => {
      scrollingRef.current = true;
      clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = window.setTimeout(() => {
        scrollingRef.current = false;
      }, 150);
    };
    el.addEventListener("scroll", onScroll, true);
    return () => {
      el.removeEventListener("scroll", onScroll, true);
      clearTimeout(scrollTimerRef.current);
    };
  }, [open]);

  // ------------------------------------------------------------------
  // Keyboard navigation
  // ------------------------------------------------------------------

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      const items = getItems();
      const count = items.length;
      if (count === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setActiveIdx((prev) => Math.min(prev + 1, count - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setActiveIdx((prev) => Math.max(prev - 1, 0));
          break;
        case "Enter":
          e.preventDefault();
          e.stopPropagation();
          items[activeIdx]?.click();
          break;
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          onClose();
          break;
        default:
          if (!(e.metaKey || e.ctrlKey)) {
            const key = e.key.toLowerCase();
            const shortcutEl =
              key.length === 1
                ? (popoverRef.current?.querySelector<HTMLElement>(
                    `[data-popover-shortcut="${key}"]`,
                  ) ?? null)
                : null;
            if (shortcutEl) {
              e.preventDefault();
              e.stopPropagation();
              shortcutEl.click();
            } else {
              e.stopPropagation();
            }
          }
          break;
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [open, activeIdx, onClose, getItems]);

  // ------------------------------------------------------------------
  // Click-outside to close
  // ------------------------------------------------------------------

  useEffect(() => {
    if (!open) return;
    const handler = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        (!anchorRef?.current || !anchorRef.current.contains(target)) &&
        !target.closest?.("[data-floating-portal]")
      ) {
        onClose();
      }
    };
    window.addEventListener("pointerdown", handler, true);
    return () => window.removeEventListener("pointerdown", handler, true);
  }, [open, onClose, anchorRef]);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  if (!open || !pos) return null;

  const maxHeight = Math.floor(window.innerHeight * MAX_HEIGHT_VH);

  return createPortal(
    <PanelSurfaceFrame
      ref={(el) => {
        popoverRef.current = el;
        if (rootRef) rootRef.current = el;
      }}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
      data-floating-portal
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        zIndex: capturedZ,
        padding: "4px",
        display: "flex",
        flexDirection: "column",
        maxHeight,
        overflowY: "auto",
        ...style,
      }}
    >
      {children}
    </PanelSurfaceFrame>,
    document.body,
  );
}
